import { Add, Check, Delete, Edit, GroupAdd } from '@mui/icons-material';
import { alpha, Box, Chip, Divider, IconButton, MenuItem, Paper, Select, Typography, useTheme } from '@mui/material';
import { generateFullName } from '@sharedInterfaces/globalHelper';
import type { ICompanyUser } from '@sharedInterfaces/ICompanySettings';
import type { IPermissionGroup } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import addEmployees from '@src/APIs/graphQl/Employee/addEmployees';
import deleteEmployee from '@src/APIs/graphQl/Employee/deleteEmployee';
import editEmployee from '@src/APIs/graphQl/Employee/editEmployee';
import changeOULeader from '@src/APIs/graphQl/OrgUnit/changeOULeader';
import changeOUTitle from '@src/APIs/graphQl/OrgUnit/changeOUTitle';
import createOUs from '@src/APIs/graphQl/OrgUnit/createOUs';
import deleteOU from '@src/APIs/graphQl/OrgUnit/deleteOU';
import employeeSwitchOU from '@src/APIs/graphQl/OrgUnit/employeeSwitchOU';
import getAdGroups, { type IADGroup } from '@src/APIs/graphQl/OrgUnit/getAdGroups';
import setOUAdGroup from '@src/APIs/graphQl/OrgUnit/setOUAdGroup';
import Button from '@src/App/Components/Buttons/Button/Button';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import EmployeeSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/EmployeeSelect/EmployeeSelect';
import TextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/TextInput/TextInput';
import type Company from '@src/Objects/Company';
import type { OU } from '@src/Objects/Company';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { AddEmployeesDialog } from './AddEmployeesDialog';
import { AddOUDialog } from './AddOUDialog';
import { EditEmployeeDialog } from './EditEmployeeDialog';
import { RemoveOUDialog } from './RemoveOUDialog';

interface EditOuListProps {
    OUs: OU[];
    permissionGroups: IPermissionGroup[];
    employees: ICompanyUser[];
    setCompany: (val: Company) => void;
}

/**
 * EditOuList
 *
 * @param {EditOuListProps} props
 *
 * @returns {JSX.Element}
 */
function EditOuList(props: EditOuListProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const [target] = useState<{ id: number }>({ id: -1 });
    const [adGroups, setAdGroups] = useState<IADGroup[]>([]);

    useEffect(() => {
        getAdGroups()
            .then(setAdGroups)
            .catch(() => {
                /* MS not configured or no permission — silently ignore */
            });
    }, []);

    let draggedItem: ICompanyUser | null = null;

    const handleDragStart = (_event: React.DragEvent<HTMLDivElement>, item: ICompanyUser) => {
        draggedItem = item;
    };
    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (!event.currentTarget.id.startsWith('OU')) {
            target.id = -1;
            return;
        }
        if (event.currentTarget.id !== `OU${target}`)
            target.id = parseInt(event.currentTarget.id.replace('OU', ''), 10);
    };

    const handleDragEnd = () => {
        if (draggedItem === null || target.id === -1) return;
        const OUs = props.OUs.slice();
        const targetOU = OUs.find((ou) => ou.id === target.id);
        const sourceOU = OUs.find((ou) => ou.employees.find((e) => e.id === (draggedItem as ICompanyUser).id));
        if (targetOU === undefined) return;
        if (sourceOU && targetOU.id === sourceOU.id) return;
        employeeSwitchOU(targetOU.id, draggedItem.id).then((result) => {
            props.setCompany(result);
        });
        draggedItem = null;
        target.id = -1;
    };

    const employeesWithoutOU = props.employees.filter((employee) => {
        return !props.OUs.some((ou) => ou.employees.find((e) => e.id === employee.id));
    });
    const topOUs = props.OUs.filter((ou) => ou.parentOU === null || ou.parentOU === undefined);
    return (
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {langStrings.orgUnits}
                </Typography>
                <Divider />
                {topOUs.map((ou) => (
                    <OUComponent
                        handleDragOver={handleDragOver}
                        handleDragStart={handleDragStart}
                        handleDragEnd={handleDragEnd}
                        setCompany={props.setCompany}
                        key={ou.id}
                        ou={ou}
                        layer={0}
                        OUs={props.OUs}
                        permissionGroups={props.permissionGroups}
                        employees={props.employees}
                        adGroups={adGroups}
                    />
                ))}
                {employeesWithoutOU.length > 0 && (
                    <Box>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                            {langStrings.employeeWithoutOU}
                        </Typography>
                        <OUEmployeesList
                            id={-1}
                            OUs={props.OUs}
                            permissionGroups={props.permissionGroups}
                            setCompany={props.setCompany}
                            handleDragStart={handleDragStart}
                            handleDragEnd={handleDragEnd}
                            handleDragOver={handleDragOver}
                            employees={{ employees: employeesWithoutOU }}
                        ></OUEmployeesList>
                    </Box>
                )}
            </Box>
        </Paper>
    );
}
interface OUProps {
    OUs: OU[];
    permissionGroups: IPermissionGroup[];
    ou: OU;
    employees: ICompanyUser[];
    layer: number;
    adGroups: IADGroup[];
    setCompany: (val: Company) => void;
    handleDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
    handleDragStart: (event: React.DragEvent<HTMLDivElement>, item: ICompanyUser) => void;
    handleDragEnd: (event: React.DragEvent<HTMLDivElement>) => void;
}

function getLayerColors(layer: number, theme: any) {
    const colors = [
        { border: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.05) },
        { border: theme.palette.secondary.main, bg: alpha(theme.palette.secondary.main, 0.05) },
        { border: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.05) },
        { border: theme.palette.warning.main, bg: alpha(theme.palette.warning.main, 0.05) },
        { border: theme.palette.error.main, bg: alpha(theme.palette.error.main, 0.05) },
    ];
    return colors[layer % colors.length] || colors[0];
}
function OUComponent(props: OUProps) {
    const { openDialog } = useDialog();
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const [dialogOpen, setDialogOpen] = React.useState(false as boolean);
    // Derive members directly from props so the list reflects store updates
    // (add/delete/move) immediately. Previously this was copied into a
    // never-updated useState, which left a deleted employee visible until a
    // full page reload (F5).
    const employees = props.ou.employees;
    const [editName, setEditName] = React.useState('' as string);
    const [editLeader, setEditLeader] = React.useState(-1 as number);
    const [saving, setSaving] = React.useState(false as boolean);

    const layerColors = getLayerColors(props.layer, theme);

    const onCreateOU = () => {
        openDialog(
            <AddOUDialog
                id="AddOUDialog"
                parentOU={props.ou}
                employees={props.employees}
                onSave={async (val: OU[]): Promise<unknown> => {
                    const result = await createOUs(val).then((result) => {
                        props.setCompany(result);
                    });
                    return result;
                }}
            />,
        );
    };

    const onDeleteOU = () => {
        openDialog(
            <RemoveOUDialog
                id="RemoveOUDialog"
                ou={props.ou}
                employees={props.employees}
                onSave={async (val: boolean): Promise<unknown> => {
                    if (!val) return;
                    const result = await deleteOU(props.ou.id).then((result) => {
                        props.setCompany(result);
                    });
                    return result;
                }}
            />,
        );
    };
    return (
        <React.Fragment>
            <Paper
                elevation={2}
                sx={{
                    ml: props.layer * 2.5,
                    border: `2px solid ${layerColors.border}`,
                    bgcolor: layerColors.bg,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    borderRadius: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {editName === '' ? (
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {props.ou.title}
                            </Typography>
                        ) : (
                            <TextInput
                                value={editName}
                                onChange={(val: string): void => {
                                    setEditName(val);
                                }}
                            />
                        )}
                        <IconButton
                            size="small"
                            sx={{ display: saving ? 'none' : 'flex' }}
                            onClick={() => {
                                if (editName === '') {
                                    setEditName(props.ou.title);
                                } else {
                                    if (props.ou.title === editName) return setEditName('');
                                    setSaving(true);
                                    changeOUTitle(props.ou.id, editName)
                                        .then((result) => {
                                            props.setCompany(result);
                                            setEditName('');
                                            setSaving(false);
                                        })
                                        .catch(() => {
                                            alert(langStrings.errorChangeName);
                                            setTimeout(() => {
                                                setSaving(false);
                                            }, 3000);
                                        });
                                }
                            }}
                        >
                            {editName === '' ? <Edit /> : <Check />}
                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2">{langStrings.leader}:</Typography>
                        {editLeader === -1 ? (
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {props.ou.leader.title}
                            </Typography>
                        ) : (
                            <EmployeeSelect
                                value={editLeader}
                                onChange={(val?: number): void => {
                                    if (!val) return setEditLeader(0);
                                    setEditLeader(val);
                                }}
                            />
                        )}
                        <IconButton
                            size="small"
                            sx={{ display: saving ? 'none' : 'flex' }}
                            onClick={() => {
                                if (editLeader === -1) {
                                    setEditLeader(props.ou.leader.id > 0 ? props.ou.leader.id : 0);
                                } else {
                                    if (props.ou.leader.id === editLeader) return setEditLeader(-1);
                                    if (editLeader === 0) return setEditLeader(-1);
                                    setSaving(true);
                                    changeOULeader(props.ou.id, editLeader)
                                        .then((result) => {
                                            props.setCompany(result);
                                            setEditLeader(-1);
                                            setSaving(false);
                                        })
                                        .catch(() => {
                                            alert(langStrings.errorChangeName);
                                            setTimeout(() => {
                                                setSaving(false);
                                            }, 3000);
                                        });
                                }
                            }}
                        >
                            {editLeader === -1 ? <Edit /> : <Check />}
                        </IconButton>
                    </Box>
                    {props.adGroups.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2">AD-Gruppe:</Typography>
                            <Select
                                size="small"
                                displayEmpty
                                disabled={saving}
                                value={props.ou.adGroupId ?? ''}
                                onChange={(e) => {
                                    const val = e.target.value as string;
                                    setSaving(true);
                                    setOUAdGroup(props.ou.id, val || null)
                                        .then((result) => {
                                            props.setCompany(result);
                                            setSaving(false);
                                        })
                                        .catch(() => {
                                            alert(langStrings.errorChangeName);
                                            setSaving(false);
                                        });
                                }}
                                sx={{ minWidth: 200 }}
                            >
                                <MenuItem value="">
                                    <em>Keine Gruppe</em>
                                </MenuItem>
                                {props.adGroups.map((g) => (
                                    <MenuItem key={g.id} value={g.id}>
                                        {g.displayName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        {langStrings.employees}:
                    </Typography>
                    <OUEmployeesList
                        id={props.ou.id}
                        OUs={props.OUs}
                        permissionGroups={props.permissionGroups}
                        setCompany={props.setCompany}
                        handleDragStart={props.handleDragStart}
                        handleDragEnd={props.handleDragEnd}
                        handleDragOver={props.handleDragOver}
                        employees={{ employees }}
                    >
                        <IconButton
                            size="small"
                            color="primary"
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                '&:hover': {
                                    bgcolor: 'primary.dark',
                                },
                            }}
                            onClick={() => setDialogOpen(true)}
                        >
                            <Add />
                        </IconButton>
                    </OUEmployeesList>
                </Box>
                {props.OUs.filter((innerOU) => innerOU.parentOU === props.ou.id).map((ou) => (
                    <OUComponent
                        handleDragOver={props.handleDragOver}
                        handleDragStart={props.handleDragStart}
                        handleDragEnd={props.handleDragEnd}
                        setCompany={props.setCompany}
                        employees={props.employees}
                        key={ou.id}
                        ou={ou}
                        layer={props.layer + 1}
                        OUs={props.OUs}
                        permissionGroups={props.permissionGroups}
                        adGroups={props.adGroups}
                    />
                ))}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Button
                        size="normal"
                        text={langStrings.createOu}
                        icon={<GroupAdd />}
                        onClick={onCreateOU}
                        disabled={false}
                    />
                    <Button
                        size="normal"
                        text={langStrings.deleteOu}
                        icon={<Delete />}
                        onClick={onDeleteOU}
                        disabled={false}
                    />
                </Box>
            </Paper>
            {dialogOpen && (
                <AddEmployeesDialog
                    id="AddEmployeesDialog"
                    ou={props.ou}
                    resolve={() => {
                        setDialogOpen(false);
                    }}
                    onSave={async (emps) => {
                        const result = await addEmployees(props.ou.id, emps).then((result) => {
                            props.setCompany(result);
                            setDialogOpen(false);
                        });
                        return result;
                    }}
                    permissionGroups={props.permissionGroups}
                />
            )}
        </React.Fragment>
    );
}
interface OUEmployeesListProps {
    id: number;
    OUs: OU[];
    permissionGroups: IPermissionGroup[];
    employees: { employees: ICompanyUser[] };
    children?: React.ReactNode;
    setCompany: (val: Company) => void;
    handleDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
    handleDragStart: (event: React.DragEvent<HTMLDivElement>, item: ICompanyUser) => void;
    handleDragEnd: (event: React.DragEvent<HTMLDivElement>) => void;
}
/**
 * OUEmployeesList component.
 *
 * @param {OUEmployeesListProps} props - The props object for the OUEmployeesList component.
 * @returns {JSX.Element}
 */
function OUEmployeesList(props: OUEmployeesListProps) {
    const { openDialog } = useDialog();
    const theme = useTheme();

    return (
        <Box
            id={`OU${props.id}`}
            onDragOver={props.handleDragOver}
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                p: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                minHeight: 48,
            }}
        >
            {props.employees.employees.map((e) => (
                <Chip
                    key={e.id}
                    label={generateFullName(e.firstname, e.lastname)}
                    variant="outlined"
                    clickable
                    draggable
                    onClick={() => {
                        openDialog(
                            <EditEmployeeDialog
                                id="EditEmployeeDialog"
                                OUs={props.OUs}
                                employee={e as ICompanyUser}
                                onDelete={async (emp) => {
                                    const result = await deleteEmployee(emp.id).then((result) => {
                                        props.setCompany(result);
                                    });
                                    return result;
                                }}
                                onSave={async (emp, ouId) => {
                                    const result = await editEmployee(
                                        emp.id,
                                        emp.firstname,
                                        emp.lastname,
                                        ouId,
                                        emp.permissionGroup.id,
                                        emp.permissions.admin,
                                        emp.gender,
                                    ).then((result) => {
                                        props.setCompany(result);
                                    });
                                    return result;
                                }}
                                permissionGroups={props.permissionGroups}
                            />,
                        );
                    }}
                    onDragStart={(event) => props.handleDragStart(event as any, e)}
                    onDragEnd={(event) => props.handleDragEnd(event as any)}
                    sx={{
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        borderColor: theme.palette.success.main,
                        '&:hover': {
                            bgcolor: alpha(theme.palette.success.main, 0.2),
                        },
                        cursor: 'pointer',
                    }}
                />
            ))}
            {props.children}
        </Box>
    );
}
export default EditOuList;
